import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { updateToken, resetPersistedUIState } from '../stores/PersistedUI.js';
import { resetUIState } from '../stores/UISlice.js';
import { debounce, SYNC_PUSH_DEBOUNCE_MS } from './debounce.js';
import { type GlobalSyncSource, getGlobalSyncSources } from './GlobalSyncRegistry.js';

export const globalSyncListenerMiddleware = createListenerMiddleware();

let pollHandles: ReturnType<typeof setInterval>[] = [];
let running = false;

// read through a function, rather than the `running` variable directly, so
// the checks below don't get narrowed away as "always true/false" - `await`s
// in `startGlobalSyncs` give `stopGlobalSyncs` a chance to flip it in between
const isRunning = () => running;

const logSyncError = (key: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`global sync: sync cycle failed for ${key}`, error);
};

const stopGlobalSyncs = () => {
  running = false;

  for (const handle of pollHandles) {
    clearInterval(handle);
  }
  pollHandles = [];
};

// runs one source's initial sync, then (if still logged in) registers its
// poller - factored out so tiers below can run every source in a tier
// concurrently instead of one at a time
const runSource = async (source: GlobalSyncSource): Promise<void> => {
  await source.sync().catch((error) => logSyncError(source.key, error));

  // a logout may have arrived while the initial sync above was in
  // flight - don't register a poller for a source we've already stopped
  if (!isRunning()) return;

  const handle = setInterval(() => {
    void source.sync().catch((error) => logSyncError(source.key, error));
  }, source.intervalMs);
  pollHandles.push(handle);
};

// groups sources by ascending `priority` (default 0) so tiers that don't
// depend on each other run their initial sync in parallel rather than one
// giant sequential chain - see registerSyncSources.ts for which sources
// actually need a later tier, and why
const groupByPriorityTier = (sources: GlobalSyncSource[]): GlobalSyncSource[][] => {
  const tiers = new Map<number, GlobalSyncSource[]>();
  for (const source of sources) {
    const priority = source.priority ?? 0;
    const tier = tiers.get(priority);
    if (tier) {
      tier.push(source);
    } else {
      tiers.set(priority, [source]);
    }
  }

  return Array.from(tiers.entries())
    .sort(([a], [b]) => a - b)
    .map(([, tier]) => tier);
};

const startGlobalSyncs = async () => {
  if (isRunning()) return;
  running = true;

  for (const tier of groupByPriorityTier(getGlobalSyncSources())) {
    await Promise.all(tier.map(runSource));

    // a logout may have arrived while this tier's initial syncs were in
    // flight - don't start the next tier for a run we've already stopped
    if (!isRunning()) return;
  }
};

const debouncedTriggers = new Map<string, () => void>();

const getDebouncedTrigger = (source: GlobalSyncSource): (() => void) => {
  let trigger = debouncedTriggers.get(source.key);
  if (!trigger) {
    trigger = debounce(source.sync, SYNC_PUSH_DEBOUNCE_MS, (error) => logSyncError(source.key, error));
    debouncedTriggers.set(source.key, trigger);
  }

  return trigger;
};

/**
 * Called after a local write to a globally-synced collection (e.g.
 * `queueLocalSettingUpdate`) to push it soon, rather than waiting for
 * that collection's next scheduled poll. Debounced per source, so a
 * burst of edits collapses into one push. A no-op while logged out - the
 * edit stays queued in dexie and goes out with the next login's initial
 * sync (or the interval, once one is running).
 */
export const triggerGlobalSync = (key: string): void => {
  if (!isRunning()) return;

  const source = getGlobalSyncSources().find((candidate) => candidate.key === key);
  if (!source) return;

  getDebouncedTrigger(source)();
};

globalSyncListenerMiddleware.startListening({
  actionCreator: updateToken,
  effect: async (action) => {
    if (action.payload) {
      await startGlobalSyncs();
    } else {
      stopGlobalSyncs();
    }
  },
});

globalSyncListenerMiddleware.startListening({
  matcher: isAnyOf(resetPersistedUIState, resetUIState),
  effect: () => {
    stopGlobalSyncs();
  },
});
